import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	As,
	ButtonGroup,
	Icon,
	useColorModeValue,
} from '@chakra-ui/react';
import {
	IconAlertCircle,
	IconAlertTriangle,
	IconCircleCheck,
	IconX,
	TablerIcon,
} from '@tabler/icons';
import { ButtonComponent } from 'components/buttons/ButtonComponent';
import React, { ReactNode, useState } from 'react';
import { globalStyles } from 'theme/styles';
type Status = 'info' | 'warning' | 'success' | 'error';
interface IAlertComponentProps {
	title?: string;
	description?: string | JSX.Element;
	status?: Status;
	noIcon?: boolean;
	icon?: As | ReactNode;
	iconColor?: string;
	iconSize?: number;
	hasCloseButton?: boolean;
	actionClose?: Function;
	mb?: string | number;
	[key: string]: any;
}

interface IStatusAlert {
	icon: TablerIcon;
	color: string;
	color2: string;
	defaultTitle?: string;
}

const statusAlerts: Record<Status, IStatusAlert> = {
	info: {
		icon: IconAlertCircle,
		color: globalStyles.colors.easyBLUE['300'],
		color2: globalStyles.colors.easyBLUE['300'],
	},
	warning: {
		icon: IconAlertTriangle,
		color: globalStyles.colors.orange['500'],
		color2: globalStyles.colors.orange['700'],
	},
	success: {
		icon: IconCircleCheck,
		color: globalStyles.colors.green['500'],
		color2: globalStyles.colors.green['500'],
	},
	error: {
		icon: IconAlertCircle,
		color: globalStyles.colors.easyRED['400'],
		color2: globalStyles.colors.easyRED['400'],
	},
};

export const AlertComponent = (props: IAlertComponentProps) => {
	const statusAlert = statusAlerts[props.status || 'info'];
	const {
		title = statusAlert.defaultTitle,
		description,
		status,
		noIcon,
		icon,
		iconColor,
		iconSize = 6,
		hasCloseButton,
		actionClose,
		mb = '10px',
		...rest
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(
		hasCloseButton === undefined || hasCloseButton === true
	);

	const ArqBg = useColorModeValue('secondaryGray.100', 'whiteAlpha.100');

	const actionCloseAlert = () => {
		setIsOpen(false);
		actionClose && actionClose();
	};

	const renderIcon = (
		customIcon: As | ReactNode,
		size: number,
		color: string
	) => {
		if (React.isValidElement(customIcon)) {
			return customIcon;
		}

		if (
			typeof customIcon === 'function' ||
			(typeof customIcon === 'object' && customIcon !== null)
		) {
			return (
				<Icon
					as={customIcon as As}
					boxSize={size}
					color={color}
				/>
			);
		}

		return null;
	};

	const getAlertIcon = () => {
		if (noIcon) return null;

		if (icon) {
			return renderIcon(icon, iconSize, iconColor || statusAlert.color);
		}

		return (
			<AlertIcon
				as={statusAlert.icon}
				color={iconColor || statusAlert.color}
				boxSize={`${iconSize * 4}px` || '24px'}
			/>
		);
	};

	return isOpen ? (
		<Alert
			mb={mb}
			status={status}
			backgroundColor={status === undefined ? ArqBg : ''}
			minH={'60px'}
			borderRadius='16px'
			w={status !== undefined ? '100%' : '40%'}
			justifyContent={status !== undefined ? 'start' : ''}
			{...rest}
		>
			{getAlertIcon()}
			<AlertTitle
				color={statusAlert.color}
				mr={'12px'}
			>
				{title}
			</AlertTitle>
			<AlertDescription color={statusAlert.color2}>
				{description}
			</AlertDescription>
			{hasCloseButton && (
				<ButtonGroup marginLeft={'auto'}>
					<ButtonComponent
						type={'icon'}
						colorType='warning'
						iconType={<IconX size='20px' />}
						size={4}
						color={statusAlert.color}
						onClick={actionCloseAlert}
					/>
				</ButtonGroup>
			)}
		</Alert>
	) : (
		<></>
	);
};
